import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
    ActionIcon,
    Avatar,
    Box,
    Button,
    Divider,
    Group,
    Loader,
    Modal,
    Pagination,
    Select,
    Stack,
    Text,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useEffect, useMemo, useState, useTransition } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { PersonInfo } from '../../../models/person.model';
import { PersonService } from '../../../services/personService';
import { isPersonCurrentlyUnassigned } from '../../../utils/availabilityUtils';
import { TIER_THRESHOLDS } from '../../../utils/selectionManager';

type ReviewSortField = 'name' | 'email' | 'assignment' | 'availability' | 'cvUpdateDate' | 'profileCompleteness' | 'inactive';
type ReviewSortDirection = 'asc' | 'desc';

interface ReviewSelectionModalProps {
    opened: boolean;
    selectedIds: Set<number>;
    onClose: () => void;
    onDeselectByIds: (ids: number[]) => void;
    onReselectByIds: (ids: number[]) => void;
    onClearAll: () => void;
    onReviewCompleted: (finalIds: number[]) => void;
}

interface LastUnselectOperation {
    ids: number[];
    persons: PersonInfo[];
    label: string;
}

const resolveImageSrc = (image: unknown): string | undefined => {
    if (typeof image !== 'string' || !image) {
        return undefined;
    }

    if (image.startsWith('data:image') || image.startsWith('http://') || image.startsWith('https://')) {
        return image;
    }

    return `data:image/jpeg;base64,${image}`;
};

const getAssignment = (person: PersonInfo): string => (isPersonCurrentlyUnassigned(person.availabilityDate) ? 'Unassigned' : 'Assigned');

const getAvailability = (person: PersonInfo): string => {
    if (!person.availabilityDate) {
        return '-';
    }

    const date = new Date(person.availabilityDate);
    if (Number.isNaN(date.getTime()) || date.getFullYear() <= 1) {
        return '-';
    }

    return date.toLocaleDateString();
};

const getCvUpdate = (person: PersonInfo): string => {
    if (!person.cvUpdateDate) {
        return 'Not specified';
    }

    const date = new Date(person.cvUpdateDate);
    if (Number.isNaN(date.getTime()) || date.getFullYear() <= 1) {
        return 'Not specified';
    }

    return date.toLocaleDateString();
};

const getProfileCompleteness = (person: PersonInfo): string => {
    if (typeof person.finalScoring !== 'number') {
        return '--';
    }

    return `${Math.round(person.finalScoring * 100)}%`;
};

const getReviewGuidance = (count: number, t: (key: string, params?: Record<string, string>, defaultValue?: string) => string) => {
    if (count <= TIER_THRESHOLDS.DIRECT) {
        return {
            color: 'teal.7',
            text: t('REVIEW_GUIDANCE_DIRECT', { count: count.toString(), suffix: count === 1 ? '' : 's' })
        };
    }

    if (count <= TIER_THRESHOLDS.WARNING) {
        return {
            color: 'orange.8',
            text: t('REVIEW_GUIDANCE_WARNING', {
                min: (TIER_THRESHOLDS.DIRECT + 1).toString(),
                max: TIER_THRESHOLDS.WARNING.toString(),
            })
        };
    }

    if (count <= TIER_THRESHOLDS.MANDATORY_REVIEW) {
        return {
            color: 'orange.9',
            text: t('REVIEW_GUIDANCE_MANDATORY', {
                min: (TIER_THRESHOLDS.WARNING + 1).toString(),
                max: TIER_THRESHOLDS.MANDATORY_REVIEW.toString(),
            })
        };
    }

    const toRemove = count - TIER_THRESHOLDS.MANDATORY_REVIEW;
    return {
        color: 'red.7',
        text: t('REVIEW_GUIDANCE_HARD_BLOCK', { count: toRemove.toString(), suffix: toRemove === 1 ? '' : 's' })
    };
};

export const ReviewSelectionModal = ({
    opened,
    selectedIds,
    onClose,
    onDeselectByIds,
    onReselectByIds,
    onClearAll,
    onReviewCompleted,
}: ReviewSelectionModalProps) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const useCardsLayout = useMediaQuery('(max-width: 84em)');
    const [isSelectionSyncPending, startSelectionSyncTransition] = useTransition();
    const [isLoading, setIsLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [sortField, setSortField] = useState<ReviewSortField>('name');
    const [sortDirection, setSortDirection] = useState<ReviewSortDirection>('asc');
    const [page, setPage] = useState(1);
    const [selectedPersons, setSelectedPersons] = useState<PersonInfo[]>([]);
    const [lastUnselectOperation, setLastUnselectOperation] = useState<LastUnselectOperation | null>(null);

    const pageSize = useCardsLayout ? 10 : 16;
    const selectedIdsArray = useMemo(() => Array.from(selectedIds), [selectedIds]);
    const areBulkActionsDisabled = isLoading || isSelectionSyncPending;
    const sortOptions = useMemo(() => [
        { value: 'name', label: t('REVIEW_SORT_NAME') },
        { value: 'email', label: t('REVIEW_SORT_EMAIL') },
        { value: 'assignment', label: t('REVIEW_SORT_ASSIGNMENT') },
        { value: 'availability', label: t('REVIEW_SORT_AVAILABILITY') },
        { value: 'cvUpdateDate', label: t('REVIEW_SORT_CV_UPDATE') },
        { value: 'profileCompleteness', label: t('REVIEW_SORT_PROFILE_COMPLETENESS') },
        { value: 'inactive', label: t('REVIEW_SORT_INACTIVE_STATUS') },
    ], [t]);

    useEffect(() => {
        if (!opened) {
            setSearchText('');
            setSortField('name');
            setSortDirection('asc');
            setPage(1);
            setSelectedPersons([]);
            setLastUnselectOperation(null);
            setIsLoading(false);
            return;
        }

        if (selectedIdsArray.length === 0) {
            setSelectedPersons([]);
            setLastUnselectOperation(null);
            return;
        }

        const personService = new PersonService();
        let isCancelled = false;

        const loadSelected = async () => {
            setIsLoading(true);
            try {
                const data = await personService.getPersonsByIds({
                    personIds: selectedIdsArray,
                    inactive: true,
                    sortField: 'firstName',
                    sortDirection: 'Ascending',
                });

                if (!isCancelled) {
                    setSelectedPersons(data);
                }
            } catch (error) {
                if (!isCancelled) {
                    console.error('Failed to load selected persons for review:', error);
                    setSelectedPersons([]);
                }
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadSelected();

        return () => {
            isCancelled = true;
        };
    }, [opened]);

    useEffect(() => {
        if (!opened) {
            return;
        }

        // Keep local modal state aligned with parent selection changes without refetching.
        setSelectedPersons(prev => {
            if (prev.length === selectedIds.size && prev.every(person => selectedIds.has(person.id))) {
                return prev;
            }

            return prev.filter(person => selectedIds.has(person.id));
        });
    }, [opened, selectedIds]);

    useEffect(() => {
        if (!opened) {
            return;
        }

        setPage(1);
    }, [searchText, sortField, sortDirection, opened]);

    const filteredPersons = useMemo(() => {
        const search = searchText.trim().toLowerCase();
        const visible = selectedPersons;

        const byText = search.length === 0
            ? visible
            : visible.filter((person) => {
                const haystack = `${person.firstName} ${person.lastName} ${person.email} ${getAssignment(person)} ${getAvailability(person)} ${person.officeLocationName ?? ''}`.toLowerCase();
                return haystack.includes(search);
            });

        const sorted = [...byText].sort((a, b) => {
            const direction = sortDirection === 'asc' ? 1 : -1;

            const compareText = (left: string, right: string) => left.localeCompare(right) * direction;

            if (sortField === 'name') {
                return compareText(`${a.firstName} ${a.lastName}`, `${b.firstName} ${b.lastName}`);
            }
            if (sortField === 'email') {
                return compareText(a.email ?? '', b.email ?? '');
            }
            if (sortField === 'assignment') {
                return compareText(getAssignment(a), getAssignment(b));
            }
            if (sortField === 'availability') {
                return compareText(getAvailability(a), getAvailability(b));
            }
            if (sortField === 'cvUpdateDate') {
                return compareText(getCvUpdate(a), getCvUpdate(b));
            }
            if (sortField === 'profileCompleteness') {
                const left = typeof a.finalScoring === 'number' ? a.finalScoring : -1;
                const right = typeof b.finalScoring === 'number' ? b.finalScoring : -1;
                return (left - right) * direction;
            }

            return compareText(a.inactive ? 'yes' : 'no', b.inactive ? 'yes' : 'no');
        });

        return sorted;
    }, [searchText, selectedPersons, sortDirection, sortField]);

    const totalPages = Math.max(1, Math.ceil(filteredPersons.length / pageSize));

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const pagedPersons = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredPersons.slice(start, start + pageSize);
    }, [filteredPersons, page, pageSize]);
    const reviewGuidance = getReviewGuidance(selectedIds.size, t);

    const handleUnselectIds = (ids: number[], label: string) => {
        if (ids.length === 0) {
            return;
        }

        const idSet = new Set(ids);
        const removedPersons = selectedPersons.filter(person => idSet.has(person.id));
        if (removedPersons.length > 0) {
            setLastUnselectOperation({
                ids: Array.from(idSet),
                persons: removedPersons,
                label,
            });
        }

        setSelectedPersons(prev => prev.filter(person => !idSet.has(person.id)));
        startSelectionSyncTransition(() => {
            onDeselectByIds(ids);
        });
    };

    const handleUnselectCurrentPage = () => {
        handleUnselectIds(pagedPersons.map(p => p.id), `current page (${pagedPersons.length})`);
    };

    const handleUnselectFiltered = () => {
        handleUnselectIds(filteredPersons.map(p => p.id), `filtered results (${filteredPersons.length})`);
    };

    const handleClearAll = () => {
        if (selectedIds.size === 0) {
            return;
        }

        if (selectedPersons.length > 0) {
            setLastUnselectOperation({
                ids: selectedIdsArray,
                persons: selectedPersons,
                label: `all selected people (${selectedIds.size})`,
            });
        }

        setSelectedPersons([]);
        startSelectionSyncTransition(() => {
            onClearAll();
        });
    };

    const handleUndoLastUnselect = () => {
        if (!lastUnselectOperation) {
            return;
        }

        const restoredPersons = lastUnselectOperation.persons;
        const restoreIds = lastUnselectOperation.ids;

        setSelectedPersons(prev => {
            const existing = new Set(prev.map(person => person.id));
            const merged = [...prev, ...restoredPersons.filter(person => !existing.has(person.id))];
            return merged;
        });
        startSelectionSyncTransition(() => {
            onReselectByIds(restoreIds);
        });
        setLastUnselectOperation(null);
    };

    const handleDone = () => {
        onReviewCompleted(selectedIdsArray);
        onClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t('REVIEW_MODAL_TITLE', { count: selectedIds.size.toString() })}
            size={isMobile ? '100%' : '95vw'}
            fullScreen={isMobile}
            centered
            closeOnEscape
            closeOnClickOutside={false}
            styles={{
                content: {
                    maxWidth: isMobile ? '100%' : 1320,
                    height: isMobile ? '100vh' : '90vh',
                },
                body: {
                    paddingTop: 8,
                    height: isMobile ? 'calc(100vh - 60px)' : 'calc(90vh - 60px)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
            }}
        >
            <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
                <Group justify="space-between" align="center" wrap="wrap">
                    <TextInput
                        placeholder={t('REVIEW_SEARCH_PLACEHOLDER')}
                        value={searchText}
                        onChange={(event) => setSearchText(event.currentTarget.value)}
                        rightSection={searchText ? (
                            <ActionIcon
                                variant="subtle"
                                color="gray"
                                aria-label={t('REVIEW_SEARCH_CLEAR_ARIA')}
                                onClick={() => setSearchText('')}
                            >
                                <XMarkIcon style={{ width: 14, height: 14 }} />
                            </ActionIcon>
                        ) : null}
                        style={{ flex: 1, minWidth: 240 }}
                    />
                    <Group gap="xs">
                        <Select
                            data={sortOptions}
                            value={sortField}
                            onChange={(value) => setSortField((value as ReviewSortField) || 'name')}
                            allowDeselect={false}
                            w={220}
                        />
                        <Select
                            data={[
                                { value: 'asc', label: 'Ascending' },
                                { value: 'desc', label: 'Descending' },
                            ]}
                            value={sortDirection}
                            onChange={(value) => setSortDirection((value as ReviewSortDirection) || 'asc')}
                            allowDeselect={false}
                            w={210}
                        />
                    </Group>
                </Group>

                <Group gap="xs" wrap="wrap">
                    <Button
                        size="xs"
                        variant="light"
                        color="brand"
                        onClick={handleUnselectCurrentPage}
                        disabled={areBulkActionsDisabled || pagedPersons.length === 0}
                    >
                        {t('REVIEW_UNSELECT_CURRENT_PAGE', { count: pagedPersons.length.toString() })}
                    </Button>
                    <Button
                        size="xs"
                        variant="light"
                        color="brand"
                        onClick={handleUnselectFiltered}
                        disabled={areBulkActionsDisabled || filteredPersons.length === 0}
                    >
                        {t('REVIEW_UNSELECT_FILTERED', { count: filteredPersons.length.toString() })}
                    </Button>
                    <Button
                        size="xs"
                        variant="light"
                        color="sopraPrimary"
                        onClick={handleClearAll}
                        loading={isSelectionSyncPending}
                        disabled={areBulkActionsDisabled || selectedIds.size === 0}
                    >
                        {t('REVIEW_CLEAR_ALL')}
                    </Button>
                </Group>

                <Text size="sm" c="dimmed">
                    {t('REVIEW_SHOWING_SUMMARY', {
                        pageCount: pagedPersons.length.toString(),
                        filteredCount: filteredPersons.length.toString(),
                        selectedCount: selectedIds.size.toString(),
                    })}
                </Text>
                <Text size="sm" c="dimmed">
                    {t('REVIEW_UNSELECT_EXPLANATION')}
                </Text>
                <Group gap="xs" align="flex-start" wrap="nowrap">
                    <Text size="sm" c={reviewGuidance.color} style={{ flex: 1 }}>
                        {reviewGuidance.text}
                    </Text>
                    <Tooltip
                        multiline
                        w={280}
                        position="bottom-end"
                        label={
                            <>
                                <Text size="xs">{t('REVIEW_TOOLTIP_DIRECT', { max: TIER_THRESHOLDS.DIRECT.toString() })}</Text>
                                <Text size="xs">{t('REVIEW_TOOLTIP_WARNING', { min: (TIER_THRESHOLDS.DIRECT + 1).toString(), max: TIER_THRESHOLDS.WARNING.toString() })}</Text>
                                <Text size="xs">{t('REVIEW_TOOLTIP_MANDATORY', { min: (TIER_THRESHOLDS.WARNING + 1).toString(), max: TIER_THRESHOLDS.MANDATORY_REVIEW.toString() })}</Text>
                                <Text size="xs">{t('REVIEW_TOOLTIP_BLOCKED', { min: (TIER_THRESHOLDS.MANDATORY_REVIEW + 1).toString() })}</Text>
                            </>
                        }
                    >
                        <ActionIcon variant="subtle" color="gray" aria-label={t('REVIEW_THRESHOLDS_ARIA')}>
                            <InformationCircleIcon width={18} height={18} />
                        </ActionIcon>
                    </Tooltip>
                </Group>

                {lastUnselectOperation && (
                    <Group
                        justify="space-between"
                        align="center"
                        p="xs"
                        style={{
                            border: '1px solid var(--mantine-color-blue-3)',
                            borderRadius: 8,
                            background: 'var(--mantine-color-blue-0)',
                        }}
                    >
                        <Text size="sm" c="blue.9">
                            {t('REVIEW_LAST_UNSELECT', { label: lastUnselectOperation.label })}
                        </Text>
                        <Button size="xs" variant="light" color="blue" onClick={handleUndoLastUnselect}>
                            {t('REVIEW_UNDO_LAST_UNSELECT')}
                        </Button>
                    </Group>
                )}

                <Divider />

                {isLoading ? (
                    <Group justify="center" py="xl">
                        <Loader size="md" />
                    </Group>
                ) : filteredPersons.length === 0 ? (
                    <Box py="xl">
                        <Text ta="center" c="dimmed">{t('REVIEW_NO_RESULTS')}</Text>
                    </Box>
                ) : (
                    <>
                        <Box
                            style={{
                                border: '1px solid var(--mantine-color-gray-3)',
                                borderRadius: 8,
                                padding: 8,
                                flex: 1,
                                minHeight: 0,
                                overflowY: 'auto',
                            }}
                        >
                            {useCardsLayout ? (
                                <Stack gap="xs">
                                    {pagedPersons.map((person) => (
                                        <Box
                                            key={`mobile-${person.id}`}
                                            p="xs"
                                            style={{
                                                border: '1px solid var(--mantine-color-gray-3)',
                                                borderRadius: 8,
                                                background: 'var(--mantine-color-body)',
                                            }}
                                        >
                                            <Group justify="space-between" align="flex-start" wrap="nowrap">
                                                <Group gap="sm" align="flex-start" wrap="nowrap" style={{ minWidth: 0 }}>
                                                    <Avatar src={resolveImageSrc(person.image)} radius="xl" />
                                                    <div style={{ minWidth: 0 }}>
                                                        <Text fw={600} truncate>{person.firstName} {person.lastName}</Text>
                                                        <Text size="sm" c="dimmed" truncate>{person.email}</Text>
                                                        <Text size="xs" c="dimmed" mt={2}>
                                                            {t('REVIEW_CARD_ASSIGNMENT')}: {getAssignment(person)} | {t('REVIEW_CARD_AVAILABILITY')}: {getAvailability(person)} | {t('REVIEW_CARD_CV_UPDATE')}: {getCvUpdate(person)}
                                                        </Text>
                                                        <Text size="xs" c="dimmed">
                                                            {t('REVIEW_CARD_PROFILE_COMPLETENESS')}: {getProfileCompleteness(person)} | {t('REVIEW_CARD_INACTIVE')}: {person.inactive ? t('COMMON_YES') : t('COMMON_NO')}
                                                        </Text>
                                                    </div>
                                                </Group>
                                                <Button
                                                    color="sopraPrimary"
                                                    variant="light"
                                                    size="xs"
                                                    onClick={() => handleUnselectIds([person.id], `${person.firstName} ${person.lastName}`)}
                                                    aria-label={t('REVIEW_UNSELECT_PERSON_ARIA', { name: `${person.firstName} ${person.lastName}` })}
                                                >
                                                    {t('REVIEW_UNSELECT')}
                                                </Button>
                                            </Group>
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <>
                                    <Box
                                        px="sm"
                                        py={6}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'minmax(230px, 2fr) minmax(90px, 0.7fr) minmax(90px, 0.7fr) minmax(90px, 0.7fr) minmax(200px, 2.5fr) minmax(70px, 0.5fr) minmax(60px, 0.5fr) minmax(120px, 0.9fr)',
                                            gap: 12,
                                            borderBottom: '1px solid var(--mantine-color-gray-3)',
                                            position: 'sticky',
                                            top: 0,
                                            background: 'var(--mantine-color-body)',
                                            zIndex: 1,
                                        }}
                                    >
                                        <Text size="xs" fw={700}>{t('REVIEW_COL_NAME')}</Text>
                                        <Text size="xs" fw={700}>{t('REVIEW_COL_ASSIGNMENT')}</Text>
                                        <Text size="xs" fw={700}>{t('REVIEW_COL_AVAILABILITY')}</Text>
                                        <Text size="xs" fw={700}>{t('REVIEW_COL_UPDATE_DATE')}</Text>
                                        <Text size="xs" fw={700}>{t('REVIEW_COL_EMAIL')}</Text>
                                        <Text size="xs" fw={700}>{t('REVIEW_COL_PROFILE')}</Text>
                                        <Text size="xs" fw={700}>{t('REVIEW_COL_INACTIVE')}</Text>
                                        <Text size="xs" fw={700}>{t('REVIEW_COL_ACTION')}</Text>
                                    </Box>

                                    {pagedPersons.map((person, index) => (
                                        <Box
                                            key={`desktop-${person.id}`}
                                            px="sm"
                                            py={6}
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'minmax(230px, 2fr) minmax(90px, 0.7fr) minmax(90px, 0.7fr) minmax(90px, 0.7fr) minmax(200px, 2.5fr) minmax(70px, 0.5fr) minmax(60px, 0.5fr) minmax(120px, 0.9fr)',
                                                gap: 12,
                                                alignItems: 'center',
                                                borderBottom: '1px solid var(--mantine-color-gray-2)',
                                                background: index % 2 === 1 ? 'var(--mantine-color-default-hover)' : 'transparent',
                                            }}
                                        >
                                            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                                                <Avatar src={resolveImageSrc(person.image)} radius="xl" size="sm" />
                                                <Text size="sm" fw={600} truncate>{person.firstName} {person.lastName}</Text>
                                            </Group>
                                            <Text size="sm">{getAssignment(person)}</Text>
                                            <Text size="sm">{getAvailability(person)}</Text>
                                            <Text size="sm">{getCvUpdate(person)}</Text>
                                            <Text size="sm" truncate>{person.email}</Text>
                                            <Text size="sm">{getProfileCompleteness(person)}</Text>
                                            <Text size="sm">{person.inactive ? t('COMMON_YES') : t('COMMON_NO')}</Text>
                                            <Button
                                                color="sopraPrimary"
                                                variant="light"
                                                size="xs"
                                                fullWidth
                                                onClick={() => handleUnselectIds([person.id], `${person.firstName} ${person.lastName}`)}
                                                aria-label={t('REVIEW_UNSELECT_PERSON_ARIA', { name: `${person.firstName} ${person.lastName}` })}
                                            >
                                                {t('REVIEW_UNSELECT')}
                                            </Button>
                                        </Box>
                                    ))}
                                </>
                            )}
                        </Box>

                        <Group justify="space-between" align="center">
                            <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
                        </Group>
                    </>
                )}

                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose}>{t('REVIEW_EXIT')}</Button>
                    <Button color="brand" onClick={handleDone} loading={isSelectionSyncPending} disabled={isSelectionSyncPending || isLoading}>{t('REVIEW_DONE')}</Button>
                </Group>
            </Stack>
        </Modal>
    );
};
